'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import { GROWTH_TOUR_HREFS, growthTourDoneStorageKey, pathTourStops, type GrowthFocus, type PathTourStop } from '@communio/host-growth-guide';
import {
  emptyGrowthTourState,
  growthTourActiveStorageKey,
  growthTourBack,
  growthTourNext,
  parseGrowthTourActive,
  startGrowthTour,
  type GrowthTourState,
} from '@communio/growth-path-tour';
import { slugFromHostname } from '@/platform/navTour/navTourPersistence';

const ACTIVE_PREFIX = 'communio:nav-tour:growth-active';

type GrowthPathTourValue = {
  active: boolean;
  focus: GrowthFocus | '';
  step: PathTourStop | null;
  stepIndex: number;
  stepCount: number;
  stepIds: string[];
  start: (focus: GrowthFocus) => void;
  goNext: () => void;
  goBack: () => void;
  finish: () => void;
  setStepIndex: (index: number) => void;
};

const GrowthPathTourContext = createContext<GrowthPathTourValue>({
  active: false,
  focus: '',
  step: null,
  stepIndex: 0,
  stepCount: 0,
  stepIds: [],
  start: () => {},
  goNext: () => {},
  goBack: () => {},
  finish: () => {},
  setStepIndex: () => {},
});

function currentSlug(): string {
  if (typeof window === 'undefined') return 'local';
  return slugFromHostname(window.location.hostname);
}

function activeKey(slug: string): string {
  return growthTourActiveStorageKey(ACTIVE_PREFIX, slug);
}

function readActive(): GrowthTourState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(activeKey(currentSlug()));
    if (!raw) return null;
    const parsed = parseGrowthTourActive(JSON.parse(raw));
    if (!parsed) return null;
    return { active: true, focus: parsed.focus, stepIndex: parsed.stepIndex };
  } catch {
    return null;
  }
}

function writeActive(state: GrowthTourState) {
  if (typeof window === 'undefined' || !state.focus) return;
  try {
    window.sessionStorage.setItem(activeKey(currentSlug()), JSON.stringify({ focus: state.focus, stepIndex: state.stepIndex }));
  } catch {
    /* ignore */
  }
}

function clearActive() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(activeKey(currentSlug()));
  } catch {
    /* ignore */
  }
}

function navigateStop(router: ReturnType<typeof useRouter>, stop: PathTourStop | undefined) {
  if (!stop) return;
  const href = GROWTH_TOUR_HREFS[stop.id]?.web;
  if (href) router.push(href);
}

export function GrowthPathTourProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const [state, setState] = useState<GrowthTourState>(emptyGrowthTourState);

  useEffect(() => {
    const saved = readActive();
    if (saved) setState(saved);
  }, []);

  const stops = useMemo(() => (state.focus ? pathTourStops(state.focus) : []), [state.focus]);
  const step = state.active && stops.length ? stops[Math.min(state.stepIndex, stops.length - 1)] : null;
  const stepIds = useMemo(() => stops.map((s) => s.id), [stops]);

  const apply = useCallback((next: GrowthTourState) => {
    setState(next);
    if (next.active && next.focus) writeActive(next);
    else clearActive();
  }, []);

  const finish = useCallback(() => {
    if (state.focus && typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(growthTourDoneStorageKey(state.focus, slugFromHostname(window.location.hostname)), '1');
      } catch {
        /* ignore */
      }
    }
    apply(emptyGrowthTourState());
  }, [apply, state.focus]);

  const start = useCallback(
    (next: GrowthFocus) => {
      const started = startGrowthTour(next);
      if (!started) return;
      apply(started);
      navigateStop(router, pathTourStops(started.focus as GrowthFocus)[0]);
    },
    [apply, router],
  );

  const goNext = useCallback(() => {
    const result = growthTourNext(state, stops.length);
    if (result.finished) {
      finish();
      return;
    }
    apply(result.state);
    navigateStop(router, stops[result.state.stepIndex]);
  }, [apply, finish, router, state, stops]);

  const goBack = useCallback(() => {
    const next = growthTourBack(state);
    apply(next);
    navigateStop(router, stops[next.stepIndex]);
  }, [apply, router, state, stops]);

  const persistIndex = useCallback(
    (index: number) => {
      apply({ ...state, stepIndex: index });
    },
    [apply, state],
  );

  const value = useMemo(
    () => ({
      active: state.active,
      focus: state.focus,
      step,
      stepIndex: state.stepIndex,
      stepCount: stops.length,
      stepIds,
      start,
      goNext,
      goBack,
      finish,
      setStepIndex: persistIndex,
    }),
    [finish, goBack, goNext, persistIndex, start, state.active, state.focus, state.stepIndex, step, stepIds, stops.length],
  );

  return <GrowthPathTourContext.Provider value={value}>{children}</GrowthPathTourContext.Provider>;
}

export function useGrowthPathTour(): GrowthPathTourValue {
  return useContext(GrowthPathTourContext);
}

export function useGrowthPathTourActive(): boolean {
  return useContext(GrowthPathTourContext).active;
}

export function useStartGrowthPathTour(): (focus: GrowthFocus) => void {
  return useContext(GrowthPathTourContext).start;
}
